import axios, { AxiosResponse } from 'axios';
import { FCAValidationRequest, FCAValidationResponse } from '../types';
import { ENDPOINTS } from '../config/endpoints';

// Use centralized endpoint configuration
const FCA_VALIDATION_ENDPOINT = ENDPOINTS.FCA_VALIDATION_WEBHOOK;

// Timeout for API calls (15 seconds - increased for better reliability)
const API_TIMEOUT = 15000;

// Create axios instance with default config
const apiClient = axios.create({
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Validates an FCA number using the Azure Function
 * @param fcaNumber - The FCA number to validate
 * @returns Promise with validation response
 */
export const validateFCANumber = async (fcaNumber: string): Promise<FCAValidationResponse> => {
  try {
    // Clean and validate input
    const cleanFcaNumber = fcaNumber.trim().replace(/\s+/g, '');
    
    if (!cleanFcaNumber) {
      throw new Error('FCA number is required');
    }

    // Basic format validation (FCA numbers are typically 6-8 digits)
    if (!/^\d{6,8}$/.test(cleanFcaNumber)) {
      return {
        success: false,
        fca_number: cleanFcaNumber,
        valid: false,
        error: 'Invalid FCA number format. Please enter a 6-8 digit number.',
        message: 'FCA numbers should be 6-8 digits long'
      };
    }

    const requestData: FCAValidationRequest = {
      fca_number: cleanFcaNumber
    };

    const response: AxiosResponse<FCAValidationResponse> = await apiClient.post(
      FCA_VALIDATION_ENDPOINT,
      requestData
    );

    // Handle both new success format and legacy format
    if (response.data.status === 'success' || response.data.success === true) {
      return {
        success: true,
        fca_number: response.data.fca_number || cleanFcaNumber,
        valid: true,
        data: {
          organisationName: response.data.organisation_name || response.data.data?.organisationName,
          companiesHouseNumber: response.data.companies_house_number,
          businessType: response.data.business_type,
          authorisationStatus: response.data.authorisation_status,
          tradingNames: response.data.trading_names || [],
          individualNames: response.data.individual_names || [],
          validatedAt: response.data.validated_at,
          address: response.data.data?.address,
          status: response.data.data?.status,
          permissions: response.data.data?.permissions,
          regulators: response.data.data?.regulators
        },
        message: `Valid FCA number - ${response.data.organisation_name || response.data.data?.organisationName}`
      };
    }

    // Ensure the response has the expected structure
    if (!response.data || typeof response.data.success === 'undefined') {
      throw new Error('Invalid response format from FCA validation service');
    }

    return response.data;

  } catch (error) {
    console.error('FCA validation error:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      isAxiosError: axios.isAxiosError(error),
      status: axios.isAxiosError(error) ? error.response?.status : 'N/A',
      statusText: axios.isAxiosError(error) ? error.response?.statusText : 'N/A',
      data: axios.isAxiosError(error) ? error.response?.data : 'N/A'
    });

    // Handle different types of errors
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;

      // Handle specific HTTP status codes
      switch (status) {
        case 400:
          // Parse the actual error message from Azure Function
          const errorData = error.response?.data;
          let userMessage = 'Invalid FCA number';
          
          if (errorData) {
            // Check for specific error messages from the Azure Function
            if (typeof errorData === 'string') {
              if (errorData.includes('not found')) {
                userMessage = 'FCA number not found. Please check the number and try again.';
              } else if (errorData.includes('invalid')) {
                userMessage = 'Invalid FCA number format. Please enter a valid 6-8 digit FCA number.';
              } else {
                userMessage = 'Invalid FCA number. Please verify the number is correct.';
              }
            } else if (errorData.message) {
              // Use the message from the API response
              userMessage = errorData.message;
            } else if (errorData.error) {
              // Use the error field from the API response  
              userMessage = errorData.error;
            }
          }
          
          return {
            success: false,
            fca_number: fcaNumber.trim(),
            valid: false,
            error: 'Invalid FCA number',
            message: userMessage
          };
        
        case 429:
          return {
            success: false,
            fca_number: fcaNumber.trim(),
            valid: false,
            error: 'Rate limit exceeded',
            message: 'Too many requests. Please try again in a moment.'
          };
        
        case 500:
          return {
            success: false,
            fca_number: fcaNumber.trim(),
            valid: false,
            error: 'Server error',
            message: 'FCA validation service is temporarily unavailable'
          };
        
        case 503:
          return {
            success: false,
            fca_number: fcaNumber.trim(),
            valid: false,
            error: 'Service unavailable',
            message: 'FCA validation service is currently down for maintenance'
          };
        
        default:
          // For connection errors, provide a fallback option
          if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND' || error.message.includes('Network Error')) {
            return {
              success: false,
              fca_number: fcaNumber.trim(),
              valid: null, // null indicates service unavailable, not invalid FCA number
              error: 'Service unavailable',
              message: 'FCA validation service is currently unavailable. You can continue with manual entry.',
              allowManualEntry: true
            };
          }
          
          return {
            success: false,
            fca_number: fcaNumber.trim(),
            valid: false,
            error: 'Network error',
            message: 'Unable to connect to FCA validation service. Please check your internet connection and try again.'
          };
      }
    }

    // Handle network/timeout errors
    if (error instanceof Error) {
      if (error.message.includes('timeout')) {
        return {
          success: false,
          fca_number: fcaNumber.trim(),
          valid: false,
          error: 'Timeout error',
          message: 'Request timed out. Please try again.'
        };
      }

      return {
        success: false,
        fca_number: fcaNumber.trim(),
        valid: false,
        error: 'Validation error',
        message: error.message
      };
    }

    // Fallback error
    return {
      success: false,
      fca_number: fcaNumber.trim(),
      valid: false,
      error: 'Unknown error',
      message: 'An unexpected error occurred during FCA validation'
    };
  }
};

/**
 * Debounced FCA validation function
 * @param fcaNumber - The FCA number to validate
 * @param delay - Delay in milliseconds (default: 500ms)
 * @returns Promise with validation response
 */
export const debouncedValidateFCA = (() => {
  let timeoutId: NodeJS.Timeout;
  
  return (fcaNumber: string, delay: number = 500): Promise<FCAValidationResponse> => {
    return new Promise((resolve) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        const result = await validateFCANumber(fcaNumber);
        resolve(result);
      }, delay);
    });
  };
})();

/**
 * Test connection to FCA validation service
 * @returns Promise indicating if the service is available
 */
export const testFCAService = async (): Promise<boolean> => {
  try {
    // Use a test endpoint or a known valid FCA number for testing
    const response = await apiClient.get(
      FCA_VALIDATION_ENDPOINT.replace('/HttpTriggerFCAValidation', '/healthcheck'),
      { timeout: 5000 }
    );
    return response.status === 200;
  } catch (error) {
    console.warn('FCA service health check failed:', error);
    return false;
  }
};

const fcaValidationService = {
  validateFCANumber,
  debouncedValidateFCA,
  testFCAService
};

export default fcaValidationService;
